import { supabase } from './lib/supabase.js';

const DEFAULT_DATA_PLANE_URL = 'https://data.cleic.com';
const CURRENCY_DATA_CONTRACT_VERSION = 'currency-rates-central-data-v0.4';
const CENTRAL_DATA_PLANE_CONTRACT_VERSION = 'central-data-plane-v0.4';

const DATA_PRIVACY_CONTRACT = Object.freeze({
  noSecrets: true,
  noProviderPayloads: true,
  noRateValues: true,
  noRawRows: true,
  noRecordIds: true,
  noSql: true,
  noMonetaryValues: true,
  noContactLabels: true,
});

const CURRENCY_REPORTING_LAW = Object.freeze({
  bookDefaultCurrency: 'sovereign-accounting-currency',
  postedSourceAmounts: 'accounting-truth',
  reportingCurrency: 'presentation-and-analysis-only',
  providerLatestRoute: 'suggestion-only-never-posting-authority',
  noScopeState: 'neutral-no-currency-no-provider-request',
  amountInReportingCurrency: {
    posture: 'read-model-signal-only',
    emitWhen: 'reporting-currency-differs-or-consolidated-read-context',
    suppressWhen: 'book-default-duplicate-or-no-scope-or-unavailable-rate-basis',
    persistenceDefault: 'do-not-persist-on-posted-ledger-facts',
  },
  rounding: {
    posture: 'presentation-only',
    sequence: 'convert-and-aggregate-exact-first-round-at-final-display',
  },
});

const CURRENCY_GOVERNANCE_LAYERS = Object.freeze([
  {
    layer: 'ledger-fact',
    owns: 'source amount, source currency, locked Book-default translation, posting provenance',
    mutationAuthority: 'posted-truth-only',
    providerLatestAuthority: 'none',
  },
  {
    layer: 'rate-governance',
    owns: 'provider, cache, manual, imported, stale, degraded, and unavailable rate provenance',
    mutationAuthority: 'provenance-and-policy-only',
    providerLatestAuthority: 'suggestion-only',
  },
  {
    layer: 'reporting-display',
    owns: 'selected reporting currency, visible-other display, charts, Lens analysis, and snapshots',
    mutationAuthority: 'presentation-only',
    providerLatestAuthority: 'suggestion-only',
  },
]);

const CURRENCY_PROVENANCE_ENVELOPE = Object.freeze([
  { field: 'scope', required: true, valueEmission: 'metadata-only' },
  { field: 'sourceCurrency', required: true, valueEmission: 'metadata-only' },
  { field: 'targetCurrency', required: true, valueEmission: 'metadata-only' },
  { field: 'asOfBasis', required: true, valueEmission: 'metadata-only' },
  { field: 'rateSourceClass', required: true, valueEmission: 'metadata-only' },
  { field: 'operatorIntent', required: true, valueEmission: 'metadata-only' },
  { field: 'mutationAuthority', required: true, valueEmission: 'metadata-only' },
]);

const CURRENCY_OVERRIDE_PRECEDENCE = Object.freeze([
  {
    scope: 'global',
    owner: 'data.cleic.com',
    governs: 'canonical currency catalog, provider/cache/manual reference posture',
    mutationSurface: 'data.cleic.com',
  },
  {
    scope: 'shelf',
    owner: 'data.cleic.com',
    governs: 'shared defaults for a Shelf when multiple Books should inherit one policy',
    mutationSurface: 'data.cleic.com',
  },
  {
    scope: 'book',
    owner: 'ledger.cleic.com',
    governs: 'enabled currencies, reporting currency, and accounting overrides for posted facts',
    mutationSurface: 'ledger.cleic.com/settings/currencies',
  },
  {
    scope: 'account-display',
    owner: 'account.cleic.com',
    governs: 'user display preference only, never accounting truth',
    mutationSurface: 'account.cleic.com',
  },
]);

const CURRENCY_ROUTE_POSTURE = Object.freeze({
  providerHealth: {
    route: 'provider-health',
    routeClass: 'redacted-provider-status',
    status: 'metadata-status-only',
    auth: 'operator-auth-required',
    emission: 'metadata-status-only',
    providerPayloadEmission: 'forbidden',
    rateValueEmission: 'forbidden',
    secretEmission: 'forbidden',
    accountingMutation: 'forbidden',
    dbMutation: 'held',
    readinessMovement: 'forbidden',
  },
  latestRoute: {
    route: 'latest-route',
    routeClass: 'authenticated-rate-suggestion',
    status: 'source-contract-ready-values-held',
    auth: 'operator-auth-required',
    emission: 'metadata-status-only',
    providerPayloadEmission: 'forbidden',
    rateValueEmission: 'forbidden',
    secretEmission: 'forbidden',
    accountingMutation: 'forbidden',
    dbMutation: 'held',
    readinessMovement: 'forbidden',
  },
  convertRoute: {
    route: 'convert-route',
    routeClass: 'authenticated-rate-conversion',
    status: 'authenticated-rate-values-open',
    auth: 'operator-auth-required',
    emission: 'authenticated-sanitized-rate-value',
    providerPayloadEmission: 'forbidden',
    rateValueEmission: 'authenticated-sanitized-rate-only',
    secretEmission: 'forbidden',
    accountingMutation: 'forbidden',
    dbMutation: 'held',
    readinessMovement: 'forbidden',
  },
});

const RECEIPT_FORBIDDEN_KEY_PATTERN = /(payload|raw|secret|sql|row|record.?id|label|amount|value|rate)$/i;
const MONEY_PROVENANCE_WARNING_FIELDS = Object.freeze(['warningCode', 'warningCodes', 'warnings']);

export function getDataPlaneUrl(path = '/') {
  const base = (import.meta.env.VITE_CLEIC_DATA_URL || DEFAULT_DATA_PLANE_URL).replace(/\/+$/, '');
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${base}${suffix}`;
}

export function fallbackCurrencyDataPlaneContract() {
  return {
    contractVersion: CURRENCY_DATA_CONTRACT_VERSION,
    app: 'cleic-data',
    domain: 'currency-rates',
    sourceOfGovernance: 'data.cleic.com',
    provider: {
      primary: 'currencybeacon',
      liveSecretCustody: 'server-env-only',
      liveProviderProof: 'held-until-explicit-provider-readback',
      providerPayloadsEmitted: false,
      rawRatesEmitted: false,
      rateValuesEmitted: true,
      providerHealth: CURRENCY_ROUTE_POSTURE.providerHealth,
      latestRoute: CURRENCY_ROUTE_POSTURE.latestRoute,
      convertRoute: CURRENCY_ROUTE_POSTURE.convertRoute,
    },
    consumers: [
      {
        app: 'ledger.cleic.com',
        use: 'currency setup, rate governance, and book-scoped accounting overrides',
        source: 'data.cleic.com',
        posture: 'metadata-contract-now-authenticated-values-open',
      },
      {
        app: 'lens.cleic.com',
        use: 'currency context for actuals, model assumptions, and presentation',
        source: 'data.cleic.com',
        posture: 'metadata-contract-now-authenticated-values-open',
      },
    ],
    governanceLayers: CURRENCY_GOVERNANCE_LAYERS,
    reportingLaw: CURRENCY_REPORTING_LAW,
    provenanceEnvelope: CURRENCY_PROVENANCE_ENVELOPE,
    overridePrecedence: CURRENCY_OVERRIDE_PRECEDENCE,
    privacy: DATA_PRIVACY_CONTRACT,
    readiness: {
      centralContract: 'published',
      ledgerConsumer: 'source-wired',
      lensConsumer: 'source-wired',
      providerLiveReadback: 'authenticated-convert-route-open',
      dbRows: 'held',
      strictLiveReadinessMovementAllowed: false,
    },
  };
}

export function normalizeCurrencyDataPlaneContract(contract = fallbackCurrencyDataPlaneContract()) {
  const fallback = fallbackCurrencyDataPlaneContract();
  const provider = contract.provider || {};
  return {
    ...fallback,
    ...contract,
    contractVersion: contract.contractVersion || fallback.contractVersion,
    provider: {
      ...fallback.provider,
      ...provider,
      providerPayloadsEmitted: false,
      rawRatesEmitted: false,
      rateValuesEmitted: provider.rateValuesEmitted === true,
      providerHealth: provider.providerHealth || fallback.provider.providerHealth,
      latestRoute: provider.latestRoute || fallback.provider.latestRoute,
      convertRoute: provider.convertRoute || fallback.provider.convertRoute,
    },
    consumers: Array.isArray(contract.consumers) ? contract.consumers : fallback.consumers,
    governanceLayers: Array.isArray(contract.governanceLayers) ? contract.governanceLayers : fallback.governanceLayers,
    reportingLaw: contract.reportingLaw || fallback.reportingLaw,
    provenanceEnvelope: Array.isArray(contract.provenanceEnvelope)
      ? contract.provenanceEnvelope
      : fallback.provenanceEnvelope,
    overridePrecedence: Array.isArray(contract.overridePrecedence)
      ? contract.overridePrecedence
      : fallback.overridePrecedence,
    privacy: {
      ...fallback.privacy,
      ...(contract.privacy || {}),
      noProviderPayloads: true,
      noRateValues: true,
      noRawRows: true,
      noRecordIds: true,
      noMonetaryValues: true,
      noContactLabels: true,
    },
    readiness: {
      ...fallback.readiness,
      ...(contract.readiness || {}),
      providerLiveReadback:
        contract.readiness?.providerLiveReadback || fallback.readiness.providerLiveReadback,
      dbRows: 'held',
      strictLiveReadinessMovementAllowed: false,
    },
  };
}

export function buildLensCurrencyConsumerReceipt(contract = fallbackCurrencyDataPlaneContract()) {
  const normalized = normalizeCurrencyDataPlaneContract(contract);
  const lensConsumer = normalized.consumers.find((consumer) => consumer.app === 'lens.cleic.com') || null;
  return {
    contractVersion: normalized.contractVersion,
    centralDataPlaneVersion: CENTRAL_DATA_PLANE_CONTRACT_VERSION,
    consumer: lensConsumer?.app || 'lens.cleic.com',
    sourceOfGovernance: normalized.sourceOfGovernance,
    use: lensConsumer?.use || 'currency context for actuals, model assumptions, and presentation',
    posture: lensConsumer?.posture || 'metadata-contract-now-authenticated-values-open',
    reportingCurrencyAuthority: normalized.reportingLaw.reportingCurrency,
    postedSourceAmountsAuthority: normalized.reportingLaw.postedSourceAmounts,
    noScopeState: normalized.reportingLaw.noScopeState,
    amountInReportingCurrency: normalized.reportingLaw.amountInReportingCurrency,
    rounding: normalized.reportingLaw.rounding,
    providerLatestAuthority: normalized.reportingLaw.providerLatestRoute,
    providerConvertRoute: normalized.provider.convertRoute
      ? {
          route: normalized.provider.convertRoute.route,
          status: normalized.provider.convertRoute.status,
          emission: normalized.provider.convertRoute.emission,
          rateValueEmission: normalized.provider.convertRoute.rateValueEmission,
          accountingMutation: normalized.provider.convertRoute.accountingMutation,
          dbMutation: normalized.provider.convertRoute.dbMutation,
        }
      : null,
    privacy: normalized.privacy,
    readiness: {
      providerLiveReadback: normalized.readiness.providerLiveReadback,
      dbRows: normalized.readiness.dbRows,
      strictLiveReadinessMovementAllowed: false,
    },
  };
}

export function buildLensMoneyProvenanceReceipt(moneyProvenance, options = {}) {
  const scope = String(options.scope || moneyProvenance?.scope || '').trim();
  const hasScope = Boolean(scope);
  const basis = moneyProvenance?.currencyBasis || {};
  const sourceCurrency = hasScope
    ? moneyProvenance?.baseCurrency || moneyProvenance?.originCurrency || basis.bookDefaultCurrency || null
    : null;
  const targetCurrency = hasScope
    ? moneyProvenance?.quoteCurrency || moneyProvenance?.bookDefaultCurrency || basis.displayCurrency || basis.reportingCurrency || null
    : null;
  return pruneCurrencyReceipt({
    contractVersion: moneyProvenance?.contractVersion || 'ledger-money-provenance-currencybeacon-v1',
    scope: hasScope ? scope : 'neutral-no-scope',
    sourceCurrency,
    targetCurrency,
    asOfBasis: hasScope
      ? moneyProvenance?.asOfBasis || moneyProvenance?.rateResolution || moneyProvenance?.providerMode || 'contract'
      : null,
    rateSourceClass: hasScope
      ? moneyProvenance?.rateSourceClass || moneyProvenance?.rateSource || moneyProvenance?.sourceProvider || 'contract'
      : null,
    operatorIntent: hasScope ? 'display-reporting-presentation-only' : 'no-provider-request',
    mutationAuthority: 'presentation-only',
    accountingTruthAuthority: 'ledger-posted-source-amounts',
    reportingCurrencyAuthority: CURRENCY_REPORTING_LAW.reportingCurrency,
    noScopeState: CURRENCY_REPORTING_LAW.noScopeState,
    displayOnly: true,
    warnings: collectMoneyProvenanceWarnings(moneyProvenance),
  });
}

function collectMoneyProvenanceWarnings(moneyProvenance) {
  if (!moneyProvenance || typeof moneyProvenance !== 'object') return [];
  const warnings = [];
  for (const field of MONEY_PROVENANCE_WARNING_FIELDS) {
    const value = moneyProvenance[field];
    if (Array.isArray(value)) warnings.push(...value);
    else if (typeof value === 'string' && value.trim()) warnings.push(value.trim());
  }
  return [...new Set(warnings)];
}

function pruneCurrencyReceipt(value) {
  if (Array.isArray(value)) {
    return value.map(pruneCurrencyReceipt).filter((item) => item !== undefined);
  }
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(
    Object.entries(value)
      .filter(([key]) => !RECEIPT_FORBIDDEN_KEY_PATTERN.test(key))
      .map(([key, item]) => [key, pruneCurrencyReceipt(item)])
      .filter(([, item]) => item !== undefined),
  );
}

export async function getCurrencyDataPlaneContract() {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) {
    throw new Error('Data currency contract requires an active Cleic session.');
  }

  const response = await fetch(getDataPlaneUrl('/api/currency-rates/contract'), {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
    },
  });
  if (!response.ok) {
    throw new Error(`Data currency contract held (${response.status}).`);
  }
  return normalizeCurrencyDataPlaneContract(await response.json());
}
